import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkles, Plus, Trash2, ArrowRight, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

export default function GoalsBuilderPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedGoals, setExpandedGoals] = useState({});
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newSubGoal, setNewSubGoal] = useState({});

  useEffect(() => {
    Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/goals`)
    ]).then(([pRes, gRes]) => {
      setProject(pRes.data);
      setGoals(gRes.data);
      const expanded = {};
      gRes.data.forEach(g => { expanded[g.id] = true; });
      setExpandedGoals(expanded);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  const generateGoals = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/goals/generate`);
      setGoals(data);
      const expanded = {};
      data.forEach(g => { expanded[g.id] = true; });
      setExpandedGoals(expanded);
    } catch {
      // Fallback handled by backend
    } finally {
      setGenerating(false);
    }
  };

  const addGoal = async () => {
    if (!newGoalTitle.trim()) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/goals`, { title: newGoalTitle });
      setGoals(prev => [...prev, data]);
      setNewGoalTitle('');
      setExpandedGoals(prev => ({ ...prev, [data.id]: true }));
    } catch {}
  };

  const deleteGoal = async (goalId) => {
    try {
      await api.delete(`/goals/${goalId}`);
      setGoals(prev => prev.filter(g => g.id !== goalId));
    } catch {}
  };

  const addSubGoal = async (goalId) => {
    const title = newSubGoal[goalId]?.trim();
    if (!title) return;
    try {
      const { data } = await api.post(`/goals/${goalId}/subgoals`, { title });
      setGoals(prev => prev.map(g => g.id === goalId ? { ...g, subgoals: [...(g.subgoals || []), data] } : g));
      setNewSubGoal(prev => ({ ...prev, [goalId]: '' }));
    } catch {}
  };

  const deleteSubGoal = async (goalId, subgoalId) => {
    try {
      await api.delete(`/subgoals/${subgoalId}`);
      setGoals(prev => prev.map(g => g.id === goalId ? { ...g, subgoals: g.subgoals.filter(sg => sg.id !== subgoalId) } : g));
    } catch {}
  };

  const toggleGoal = (goalId) => {
    setExpandedGoals(prev => ({ ...prev, [goalId]: !prev[goalId] }));
  };

  if (loading) return <div className="min-h-screen bg-[#F7F9FC]"><Navbar /><div className="max-w-3xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4"><div className="h-8 bg-[#EFF3F8] rounded w-1/2" /><div className="h-4 bg-[#EFF3F8] rounded w-3/4" /></div></div></div>;

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <h1 data-testid="goals-heading" className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F2951]">Goals & Sub-goals</h1>
          <p className="text-base text-[#64748B] mt-1">Define what success looks like for <span className="font-medium text-[#0F2951]">{project?.business_name}</span></p>
          {project?.intention && (
            <div className="mt-4 p-4 bg-[#EFF3F8] rounded-xl border border-[#E2E8F0]">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B] mb-1">Your Intention</p>
              <p className="text-sm text-[#0F2951] italic">"{project.intention}"</p>
            </div>
          )}
        </div>

        {/* AI Generate */}
        <div className="mb-6">
          <Button
            data-testid="generate-goals-button"
            onClick={generateGoals}
            disabled={generating}
            className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 transition-all duration-200 hover:-translate-y-[1px]"
          >
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating with AI...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Goals with AI</>}
          </Button>
          {goals.length > 0 && <span className="ml-3 text-sm text-[#64748B]">{goals.length} goals created</span>}
        </div>

        {/* Goals List */}
        <div className="space-y-4">
          {goals.map((goal, gi) => (
            <div key={goal.id} data-testid={`goal-card-${gi}`} className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_4px_24px_rgba(44,48,46,0.04)] overflow-hidden">
              <div className="flex items-center gap-3 p-5 cursor-pointer" onClick={() => toggleGoal(goal.id)}>
                <div className="w-8 h-8 bg-[#0268C4]/10 rounded-lg flex items-center justify-center text-[#0268C4] text-sm font-bold">{gi + 1}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-base font-medium text-[#0F2951] truncate">{goal.title}</h3>
                  {goal.description && <p className="text-sm text-[#64748B] truncate">{goal.description}</p>}
                </div>
                <span className="text-xs text-[#64748B] mr-2">{(goal.subgoals || []).length} sub-goals</span>
                {expandedGoals[goal.id] ? <ChevronDown className="w-4 h-4 text-[#64748B]" /> : <ChevronRight className="w-4 h-4 text-[#64748B]" />}
                <button data-testid={`delete-goal-${gi}`} onClick={e => { e.stopPropagation(); deleteGoal(goal.id); }} className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#64748B] hover:text-[#EF4444] transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>

              {expandedGoals[goal.id] && (
                <div className="border-t border-[#E2E8F0] px-5 pb-5">
                  <div className="space-y-2 mt-4">
                    {(goal.subgoals || []).map((sg, si) => (
                      <div key={sg.id} data-testid={`subgoal-${gi}-${si}`} className="flex items-start gap-3 p-3 bg-[#F7F9FC] rounded-xl group">
                        <div className="w-5 h-5 rounded-full border-2 border-[#E2E8F0] flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0F2951]">{sg.title}</p>
                          {sg.description && <p className="text-xs text-[#64748B] mt-0.5">{sg.description}</p>}
                        </div>
                        <button onClick={() => deleteSubGoal(goal.id, sg.id)} className="p-1 rounded-lg hover:bg-[#EF4444]/10 text-[#64748B] hover:text-[#EF4444] transition-colors opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Input
                      data-testid={`add-subgoal-input-${gi}`}
                      value={newSubGoal[goal.id] || ''}
                      onChange={e => setNewSubGoal(prev => ({ ...prev, [goal.id]: e.target.value }))}
                      placeholder="Add a sub-goal..."
                      onKeyDown={e => e.key === 'Enter' && addSubGoal(goal.id)}
                      className="border-[#E2E8F0] bg-white rounded-xl h-9 text-sm"
                    />
                    <Button size="sm" onClick={() => addSubGoal(goal.id)} className="bg-[#EFF3F8] hover:bg-[#E2E8F0] text-[#0F2951] rounded-xl h-9 px-3">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Goal */}
        <div className="flex items-center gap-2 mt-4">
          <Input
            data-testid="add-goal-input"
            value={newGoalTitle}
            onChange={e => setNewGoalTitle(e.target.value)}
            placeholder="Add a custom goal..."
            onKeyDown={e => e.key === 'Enter' && addGoal()}
            className="border-[#E2E8F0] bg-white rounded-xl h-11"
          />
          <Button data-testid="add-goal-button" onClick={addGoal} className="bg-[#EFF3F8] hover:bg-[#E2E8F0] text-[#0F2951] rounded-xl h-11 px-4">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {/* Continue */}
        <div className="flex justify-end mt-8">
          <Button
            data-testid="proceed-to-audit-button"
            onClick={() => navigate(`/projects/${projectId}`)}
            className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 transition-all duration-200 hover:-translate-y-[1px]"
          >
            Continue to Project <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
